/**
 * Validação centralizada de senhas.
 * Regras aplicadas no signup e no reset.
 *
 * As regras devolvem CHAVE, não texto. O rótulo de cada uma vive no dicionário
 * (`auth.passwordRules`), porque este módulo é chamado de fora do React e não
 * tem como saber o idioma. Quem exibe é quem traduz.
 */

export const PASSWORD_MIN_LENGTH: number = 8;

export type PasswordRuleKey = 'minLength' | 'uppercase' | 'lowercase' | 'number';

interface PasswordRule {
  key: PasswordRuleKey;
  test: (p: string) => boolean;
}

interface PasswordValidationResult {
  valid: boolean;
  failures: PasswordRuleKey[];
}

export const PASSWORD_RULES: PasswordRule[] = [
  { key: 'minLength', test: (p: string) => p.length >= PASSWORD_MIN_LENGTH },
  { key: 'uppercase', test: (p: string) => /[A-Z]/.test(p) },
  { key: 'lowercase', test: (p: string) => /[a-z]/.test(p) },
  { key: 'number', test: (p: string) => /[0-9]/.test(p) },
];

/**
 * Valida uma senha contra todas as regras
 * @param {string} password
 * @returns {{ valid: boolean, failures: PasswordRuleKey[] }}
 */
export function validatePassword(password: string | null | undefined): PasswordValidationResult {
  if (!password) {
    return { valid: false, failures: PASSWORD_RULES.map((r) => r.key) };
  }
  const failures = PASSWORD_RULES.filter((r) => !r.test(password)).map((r) => r.key);
  return { valid: failures.length === 0, failures };
}

/**
 * Calcula score de força da senha (0-4)
 * @param {string} password
 * @returns {number}
 */
export function getPasswordStrength(password: string | null | undefined): number {
  if (!password) return 0;
  return PASSWORD_RULES.filter((r) => r.test(password)).length;
}
